import { UserContext } from '@providers/userContextProvider';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryBuilder } from '../utils';
import { NumberWidget } from './numberWidget';

const totalAumQuery = (activeOrganization: any) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'state',
      comparison: 'EQUALS',
      value: 'ACTIVE',
    },
  ],
  type: 'SUB_ACCOUNT',
  grouping: {
    field: null,
    interval: null,
    type: 'SINGLE_VALUE',
    valueAggregation: 'SUM',
    valueField: 'marketValueCents',
  },
});

const totalUsersQuery = (activeOrganization: any) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'marketValueCents',
      comparison: 'GREATER_THAN',
      value: '0',
    },
    {
      field: 'state',
      comparison: 'EQUALS',
      value: 'ACTIVE',
    },
  ],
  type: 'USER',
  grouping: {
    field: null,
    interval: null,
    type: 'SINGLE_VALUE',
    valueAggregation: 'VALUE_COUNT',
    valueField: 'id',
  },
});

const historyAumQuery = (activeOrganization: any) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'date',
      comparison: 'GREATER_THAN_AGO',
      value: '35',
    },
    {
      field: 'marketValueCents',
      comparison: 'GREATER_THAN',
      value: '0',
    },
  ],
  type: 'DAILY_STATS',
  grouping: {
    valueField: 'marketValueCents',
    field: 'date',
    valueAggregation: 'SUM',
    type: 'FIRST_DATE_IN_INTERVAL',
    interval: 'DAY',
  },
  sorting: {
    sortField: 'date',
    sortDesc: true,
  },
});

const historyUsersQuery = (activeOrganization: any) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'marketValueCents',
      comparison: 'GREATER_THAN',
      value: '0',
    },
    {
      field: 'createdAt',
      comparison: 'LESS_THAN',
      value: dayjs().startOf('month').format('YYYY-MM-DD'),
    },
    {
      field: 'state',
      comparison: 'EQUALS',
      value: 'ACTIVE',
    },
  ],
  type: 'USER',
  grouping: {
    field: null,
    interval: null,
    type: 'SINGLE_VALUE',
    valueAggregation: 'VALUE_COUNT',
    valueField: 'id',
  },
});

export const AverageAum = () => {
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation('homePageConfiguration');
  const [totalAum, setTotalAum] = useState<number>(0);
  const [totalUsers, setTotalUsers] = useState<number>(1);
  const [historyAum, setHistoryAum] = useState<number>(0);
  const [historyUsers, setHistoryUsers] = useState<number>(1);
  const { data: totalAumData, loading: totalAumLoading } = useQueryBuilder(
    totalAumQuery(activeOrganization),
  );
  const { data: totalUsersData, loading: totalUsersLoading } = useQueryBuilder(
    totalUsersQuery(activeOrganization),
  );
  const { data: historyAumData, loading: historyAumLoading } = useQueryBuilder(
    historyAumQuery(activeOrganization),
  );
  const { data: historyUsersData, loading: historyUsersLoading } =
    useQueryBuilder(historyUsersQuery(activeOrganization));
  const lastDayOfPreviousMonth = dayjs()
    .subtract(1, 'month')
    .endOf('month')
    .format('YYYY-MM-DD');

  useEffect(() => {
    if (totalAumData?.generateReport?.grouping) {
      setTotalAum(totalAumData?.generateReport?.grouping.value ?? 0);
    }
    if (totalUsersData?.generateReport?.grouping) {
      setTotalUsers(totalUsersData?.generateReport?.grouping.value ?? 1);
    }
    if (historyAumData?.generateReport?.grouping) {
      const lastDayOfPreviousMonthAum =
        historyAumData?.generateReport?.grouping.find(
          (item: any) =>
            dayjs(item.key).format('YYYY-MM-DD') === lastDayOfPreviousMonth,
        );
      setHistoryAum(lastDayOfPreviousMonthAum?.value ?? 0);
    }
    if (historyUsersData?.generateReport?.grouping) {
      setHistoryUsers(historyUsersData?.generateReport?.grouping.value ?? 1);
    }
  }, [
    historyAumData,
    lastDayOfPreviousMonth,
    totalAumData,
    totalUsersData,
    historyUsersData,
  ]);

  return (
    <NumberWidget
      title={t('practiceOverview.averageAum')}
      newValue={totalAum / 100 / totalUsers}
      oldValue={historyAum / 100 / historyUsers}
      loading={
        totalAumLoading ||
        historyAumLoading ||
        totalUsersLoading ||
        historyUsersLoading
      }
    />
  );
};
